import { Loader2, MousePointer2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/api/trpc';

type Props = {
  storeId: string;
  isCustomButtonsOn: boolean;
};

/**
 * Opens the store with the 30-minute link that lets the merchant pick a button there. The tab
 * opens before the link is minted: a window opened after the await is blocked.
 */
export function ShopifyPickButtonLink({ storeId, isCustomButtonsOn }: Props) {
  const { t } = useTranslation('storefront');
  const pickLink = trpc.shopifyScript.createPickLink.useMutation();

  const open = () => {
    if (pickLink.isPending) return;
    const tab = window.open('', '_blank');
    pickLink.mutateAsync({ storeId }).then(
      ({ url }) => {
        if (tab === null) {
          window.open(url, '_blank', 'noopener,noreferrer');
          return;
        }
        tab.opener = null;
        tab.location.href = url;
      },
      (error: unknown) => {
        tab?.close();
        toast.error(error instanceof Error ? error.message : t('shopify.pickButtonFailed'));
      },
    );
  };

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <div className="flex-1">
        <Label className="text-sm font-medium">{t('shopify.pickButton')}</Label>
        <p className="text-xs text-slate-600 dark:text-slate-400">{t('shopify.pickButtonHelp')}</p>
        {!isCustomButtonsOn && (
          <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">{t('shopify.pickButtonCustomButtonsOff')}</p>
        )}
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={open}
        disabled={pickLink.isPending}
        className="h-8 shrink-0 gap-1.5"
        aria-label={t('shopify.pickButton')}
      >
        {pickLink.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MousePointer2 className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}
